import {
	type ParsedTypeTag,
	type filterableFieldTypes,
} from "~/components/inspector/utils/parseTypeTag";

export type Operation = UnaryOperation | BinaryOperation;

export interface UnaryOperation {
	operation: string;
	label: string;
	arity: 1;
	emit: (var1: string) => string;
}

export interface BinaryOperation {
	operation: string;
	label: string;
	arity: 2;
	emit: (var1: string, var2: string) => string;
}

const objectOperations = {
	empty: {
		operation: "empty",
		label: "empty",
		arity: 1,
		emit: (var1: string) => `(${var1} === undefined || ${var1} === null)`,
	},
	notEmpty: {
		operation: "not-empty",
		label: "not empty",
		arity: 1,
		emit: (var1: string) => `(${var1} !== undefined && ${var1} !== null)`,
	},
} as const;

const numberOperations = {
	eq: {
		operation: "equal",
		label: "=",
		arity: 2,
		emit: (var1: string, var2: string) => `aq.op.equal(${var1}, ${var2})`,
	},
	neq: {
		operation: "not-equal",
		label: "!=",
		arity: 2,
		emit: (var1: string, var2: string) => `!aq.op.equal(${var1}, ${var2})`,
	},
	gt: {
		operation: "greater-than",
		label: ">",
		arity: 2,
		emit: (var1: string, var2: string) => `(${var1} > ${var2})`,
	},
	lt: {
		operation: "less-than",
		label: "<",
		arity: 2,
		emit: (var1: string, var2: string) => `(${var1} < ${var2})`,
	},
	gte: {
		operation: "greater-than-or-equal",
		label: "≥",
		arity: 2,
		emit: (var1: string, var2: string) => `(${var1} >= ${var2})`,
	},
	lte: {
		operation: "less-than-or-equal",
		label: "≤",
		arity: 2,
		emit: (var1: string, var2: string) => `(${var1} <= ${var2})`,
	},
} as const;

const stringOperations = {
	contains: {
		operation: "contains",
		label: "contains",
		arity: 2,
		emit: (value: string, search: string) => `aq.op.includes(${value}, ${search})`,
	},
	notContains: {
		operation: "not-contains",
		label: "doesn't contain",
		arity: 2,
		emit: (value: string, search: string) => `!aq.op.includes(${value}, ${search})`,
	},
	is: {
		operation: "is",
		label: "is",
		arity: 2,
		emit: (var1: string, var2: string) => `aq.op.equal(${var1}, ${var2})`,
	},
	isNot: {
		operation: "is-not",
		label: "is not",
		arity: 2,
		emit: (var1: string, var2: string) => `!aq.op.equal(${var1}, ${var2})`,
	},
	startsWith: {
		operation: "startswith",
		label: "starts with",
		arity: 2,
		emit: (value: string, search: string) => `aq.op.startswith(${value}, ${search})`,
	},
	notStartsWith: {
		operation: "not-startswith",
		label: "doesn't start with",
		arity: 2,
		emit: (value: string, search: string) => `!aq.op.startswith(${value}, ${search})`,
	},
	endsWith: {
		operation: "endswith",
		label: "ends with",
		arity: 2,
		emit: (value: string, search: string) => `aq.op.endswith(${value}, ${search})`,
	},
	noteEndsWith: {
		operation: "not-endswith",
		label: "doesn't end with",
		arity: 2,
		emit: (value: string, search: string) => `!aq.op.endswith(${value}, ${search})`,
	},
} as const;

export type WherePredicateOperationName = keyof typeof wherePredicateOperations;

export const isWherePredicateOperation = (k: ParsedTypeTag): k is WherePredicateOperationName => {
	return k in wherePredicateOperations;
};

export const wherePredicateOperations = {
	boolean: [numberOperations.eq],
	number: [
		numberOperations.eq,
		numberOperations.gt,
		numberOperations.lt,
		numberOperations.gte,
		numberOperations.lte,
		objectOperations.empty,
		objectOperations.notEmpty,
	],
	bigint: [
		numberOperations.eq,
		numberOperations.gt,
		numberOperations.lt,
		numberOperations.gte,
		numberOperations.lte,
		objectOperations.empty,
		objectOperations.notEmpty,
	],
	string: [
		stringOperations.contains,
		stringOperations.notContains,
		stringOperations.is,
		stringOperations.isNot,
		objectOperations.empty,
		objectOperations.notEmpty,
		stringOperations.startsWith,
		stringOperations.notStartsWith,
		stringOperations.endsWith,
		stringOperations.noteEndsWith,
		//  "match-regexp" as const,
	],
};

export type WherePredicateOperations =
	(typeof wherePredicateOperations)[(typeof filterableFieldTypes)[number]][number];

// IMPORTANT: Check that `wherePredicateOperations` isn't missing any fields from
// `filterableFieldTypes`.
// @ts-ignore
type _ = (typeof wherePredicateOperations)[(typeof filterableFieldTypes)[number]];

// IMPORTANT: Check that `filterableFieldTypes` isn't missing any fields from
//  `wherePredicateOperations`.
/* eslint-disable @typescript-eslint/ban-types */
// @ts-ignore
type __ = {
	[T in (typeof filterableFieldTypes)[number]]: {};
}[keyof typeof wherePredicateOperations];
/* eslint-enable @typescript-eslint/ban-types */
